import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { useResetPasswordMutation } from "../services/appApi";
import "./ResetPassword.css";

function ResetPassword() {
  const location = useLocation();
  const navigate = useNavigate();

  const email = location.state?.email || "";
  const resetToken = location.state?.resetToken || "";

  // Guard: redirect if no token/email in state
  useEffect(() => {
    if (!email || !resetToken) navigate("/forgot-password");
  }, [email, resetToken, navigate]);

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resetPassword, { isLoading }] = useResetPasswordMutation();
  const [errorMsg, setErrorMsg] = useState("");
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");

    // Client-side validation
    if (newPassword.length < 6) {
      setErrorMsg("Password must be at least 6 characters.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setErrorMsg("Passwords do not match.");
      return;
    }

    const { data, error } = await resetPassword({
      email,
      resetToken,
      newPassword,
    });

    if (data?.success) {
      setSuccess(true);
      setTimeout(() => navigate("/login"), 2000);
    } else {
      const msg =
        error?.data?.error ||
        data?.error ||
        "Something went wrong. Please try again.";
      setErrorMsg(msg);
    }
  };

  return (
    <div className="reset-pw-root">
      <div className="reset-pw-card">
        {/* Icon */}
        <div className="reset-pw-icon-wrap" aria-hidden="true">
          {success ? "✅" : "🔐"}
        </div>

        {success ? (
          <>
            <h1 className="reset-pw-title">Password updated!</h1>
            <p className="reset-pw-subtitle">
              Your password has been changed successfully. Redirecting you to
              login…
            </p>
            <div className="reset-pw-success" role="status">
              ✓ You can now log in with your new password.
            </div>
          </>
        ) : (
          <>
            <h1 className="reset-pw-title">Set a new password</h1>
            <p className="reset-pw-subtitle">
              Choose a strong password for your account{" "}
              <strong>{email}</strong>.
            </p>

            {errorMsg && (
              <div className="reset-pw-error" role="alert">
                <span aria-hidden="true">⚠️</span> {errorMsg}
              </div>
            )}

            <form onSubmit={handleSubmit} noValidate>
              <div className="reset-pw-field">
                <label htmlFor="new-password" className="reset-pw-label">
                  New password
                </label>
                <input
                  id="new-password"
                  type="password"
                  className="reset-pw-input"
                  placeholder="At least 6 characters"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  autoFocus
                  autoComplete="new-password"
                  minLength={6}
                />
              </div>

              <div className="reset-pw-field">
                <label htmlFor="confirm-password" className="reset-pw-label">
                  Confirm password
                </label>
                <input
                  id="confirm-password"
                  type="password"
                  className="reset-pw-input"
                  placeholder="Re-enter your new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                />
              </div>

              {/* Password match indicator */}
              {confirmPassword.length > 0 && (
                <div
                  className={`reset-pw-match ${
                    newPassword === confirmPassword
                      ? "reset-pw-match--ok"
                      : "reset-pw-match--no"
                  }`}
                >
                  {newPassword === confirmPassword
                    ? "✓ Passwords match"
                    : "✗ Passwords do not match"}
                </div>
              )}

              <button
                type="submit"
                className="reset-pw-btn"
                disabled={
                  isLoading ||
                  !newPassword ||
                  !confirmPassword ||
                  newPassword !== confirmPassword
                }
              >
                {isLoading ? (
                  <>
                    <span className="btn-spinner" aria-hidden="true" />
                    Updating…
                  </>
                ) : (
                  "Update password"
                )}
              </button>
            </form>

            <div className="reset-pw-footer">
              <Link to="/login" className="reset-pw-link">
                ← Back to Login
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default ResetPassword;
